import { setCurrentPath } from 'js/actions';
import BoxAuthenticator from 'js/components/elements/BoxAuthenticator';
import DropboxAuthenticator from 'js/components/elements/DropboxAuthenticator';
import IntuitAuthenticator from 'js/components/elements/IntuitAuthenticator';
import OAuth1aAuthenticator from 'js/components/elements/OAuth1aAuthenticator';
import OAuth2Authenticator from 'js/components/elements/OAuth2Authenticator';

import ZendeskAuthenticator from 'js/components/elements/ZendeskAuthenticator';
import lazyRetry from 'js/components/lazyRetry';
import GetDemoContainer from 'js/components/login/GetDemoContainer';
import LoginContainer from 'js/components/login/LoginContainer';
import ResetPasswordContainer from 'js/components/login/ResetPasswordContainer';
import SetPasswordContainer from 'js/components/login/SetPasswordContainer';
import SSOAuthLoadingViewContainer from 'js/components/login/sso/SSOAuthLoadingViewContainer';
import SSOAuthLoginContainer from 'js/components/login/sso/SSOAuthLoginContainer';
import PipelineFormContainer from 'js/components/pipelines/PipelineFormContainer';
import NoRouteMatchView from 'js/components/route_utils/NoRouteMatchView';
import PrivateRoute from 'js/components/route_utils/PrivateRoute';
import PublicRoute from 'js/components/route_utils/PublicRoute';
import RouteLoadingComponent from 'js/components/route_utils/RouteLoadingComponent';
import VideoDashboardContainer from 'js/components/support/video/VideoDashboardContainer';
import * as PATHS from 'js/constants/pathnames';
import { ROUTE_SUPPORT, ROUTE_SUPPORT_VIDEOS } from 'js/constants/pathnames';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { Collaborate } from 'new/components/collaborate';
import Dashboard from 'new/components/dashboard/Dashboard';
import { Discover } from 'new/components/discover';
import { ByoCreateCanvas } from 'new/components/flows/types/byo/v2-shells/canvas/ByoCreateCanvas';
import Integrate from 'new/components/integrate/Integrate';
import { AI } from 'new/modules/ai';
import { Monitor } from 'new/components/monitor';
import { TriageLinkLanding } from 'new/components/monitor/TriageChat';
import Settings from 'new/components/settings/settingsContainer/SettingsContainer';
// import { SignUp } from 'js/components/login/SignUp';
// import { VerifyEmail } from 'js/components/login/VerifyEmail';
import { Tasks } from 'new/components/tasks';
import {
  ROUTE_AI,
  ROUTE_COLLABORATE,
  ROUTE_CONNECT,
  ROUTE_DISCOVER,
  ROUTE_MARKETPLACE,
  ROUTE_MONITOR,
  ROUTE_TRIAGE_LINK,
  ROUTE_SETTINGS,
  ROUTE_TASKS,
  ROUTE_EXPRESS,
  EXPRESS_URL,
} from 'new/constants/pathnames';
import ExpressView from 'new/components/express/ExpressView';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { oldToNewMap } from 'new/OldRoutesSupport';
import { router } from 'new/routes/router';
import React, { Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import CreateCDCFlow from 'src/new/components/flows/types/cdc/CreateCDCFlow';
import { EditCDCFlow } from 'src/new/components/flows/types/cdc/EditCDCFlow';
import CreateELTFlow from 'src/new/components/flows/types/elt/CreateELTFlow';
import EditRAGFlow from './components/flows/types/genai_rag/EditRAGFlow';
import Marketplace from './components/marketplace/Marketplace';

const AsyncTokenContainer = lazyRetry(() => import('js/components/token/TokenContainer'));

export default function RootRoutes() {
  const history = useHistory();
  const isAIMenuEnabled = useFeatureFlag('UI_AI_MENU');
  const isExpressEnabled = useFeatureFlag('UI_ENABLE_EXPRESS');

  function createTitle(name: string) {
    const matches = name.match(/([A-Z][a-z]*)/g);
    return matches?.join(' ');
  }

  const dispatch = useAppDispatch();
  const currentPath = useSelector((state: any) => state.routes.currentPath);

  function setPath(newPath: string) {
    setTimeout(() => {
      dispatch(setCurrentPath(newPath));
    }, 0);
  }

  const setTitle = (location) => {
    const route = router.manager.getByPathname(location.pathname);
    if (!route) return;
    const routeTitle =
      route.pageTitle ||
      route?.header?.slugTitle ||
      route?.header?.title ||
      createTitle(route?.name);
    const appTitle = process.env.REACT_APP_SITE_TITLE || 'Nexla';
    const env = process.env.REACT_APP_DEPLOY_ENV;
    document.title = `${routeTitle} - ${appTitle}${
      env && !['prod', 'dataops', 'production'].includes(env) ? ` (${env})` : ''
    }`;
  };

  useEffect(() => {
    setTitle(history.location);
    history.listen((location) => {
      setTitle(location);
    });
  }, []);

  return (
    <Suspense fallback={<RouteLoadingComponent />}>
      <Switch>
        <PublicRoute path={PATHS.ROUTE_LOGIN} component={LoginContainer} />
        {/* <PublicRoute path={PATHS.ROUTE_SIGN_UP} component={SignUp} />
        <PublicRoute path={PATHS.ROUTE_VERIFY_EMAIL} component={VerifyEmail} /> */}
        {/* TEMP: NEX-20514: Redirecting to Express URL for self-signup */}
        <Route
          path={PATHS.ROUTE_SIGN_UP}
          render={() => {
            window.location.replace(EXPRESS_URL);
            return null;
          }}
        />
        <Route
          path={PATHS.ROUTE_VERIFY_EMAIL}
          render={() => {
            window.location.replace(EXPRESS_URL);
            return null;
          }}
        />
        <PublicRoute path={PATHS.ROUTE_RESET_PASSWORD} component={ResetPasswordContainer} />
        <PublicRoute path={PATHS.ROUTE_SET_PASSWORD} component={SetPasswordContainer} />
        <PublicRoute path={PATHS.ROUTE_SSO_LOGIN} component={SSOAuthLoginContainer} />
        <PublicRoute path={PATHS.ROUTE_SSO_AUTH_LINK} component={SSOAuthLoadingViewContainer} />
        <PublicRoute path={PATHS.ROUTE_GET_DEMO} component={GetDemoContainer} />
        <PrivateRoute exact path={'/'} component={Dashboard} />
        <PrivateRoute path={PATHS.ROUTE_DROPBOX_AUTH} component={DropboxAuthenticator} />
        <PrivateRoute path={PATHS.ROUTE_BOX_AUTH} component={BoxAuthenticator} />
        <PrivateRoute path={PATHS.ROUTE_OAUTH2_AUTH} component={OAuth2Authenticator} />
        <PrivateRoute path={PATHS.ROUTE_OAUTH_1A_AUTH} component={OAuth1aAuthenticator} />
        <PrivateRoute path={PATHS.ROUTE_ZENDESK_AUTH} component={ZendeskAuthenticator} />
        <PrivateRoute path={PATHS.ROUTE_INTUIT_AUTH} component={IntuitAuthenticator} />
        <PrivateRoute path={ROUTE_SUPPORT_VIDEOS} component={VideoDashboardContainer} />
        <PrivateRoute path={ROUTE_SUPPORT} component={VideoDashboardContainer} />
        <PrivateRoute path={PATHS.ROUTE_SHOW_TOKEN} component={AsyncTokenContainer} />
        <PrivateRoute path={PATHS.ROUTE_DOWNLOAD_CLI} component={AsyncTokenContainer} />
        <PrivateRoute path={`${PATHS.ROUTE_PIPELINES}/new`} component={PipelineFormContainer} />
        <PrivateRoute
          path={`${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/cdc/:resourceType/new`}
          component={CreateCDCFlow}
        />
        <PrivateRoute
          path={`${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/cdc/:resourceType/:id/edit`}
          component={CreateCDCFlow}
        />
        <PrivateRoute
          path={`${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/elt/:resourceType/new`}
          component={CreateELTFlow}
        />
        <PrivateRoute
          path={`${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/elt/:resourceType/:id/edit`}
          component={CreateELTFlow}
        />
        <PrivateRoute
          path={`${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/custom/new`}
          component={ByoCreateCanvas}
        />
        <PrivateRoute
          path={`${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/genai_rag/new`}
          component={EditRAGFlow}
        />
        <PrivateRoute
          path={`${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/genai_rag/:id/edit`}
          component={EditRAGFlow}
        />
        {router.manager.getRoutes('main').map((route) => (
          <PrivateRoute key={route.name} {...route} />
        ))}
        <PrivateRoute path={ROUTE_CONNECT} component={Integrate} />
        {isAIMenuEnabled && <PrivateRoute path={ROUTE_AI} component={AI} />}
        <PrivateRoute path={ROUTE_TASKS} component={Tasks} />
        <PrivateRoute path={ROUTE_DISCOVER} component={Discover} />
        <PrivateRoute path={ROUTE_SETTINGS} component={Settings} />
        <PrivateRoute path={ROUTE_COLLABORATE} component={Collaborate} />
        {/* Stable triage deep-link handler — must precede the Monitor catch-all
            so /monitor/triage isn't swallowed by it. Redirects to Integrate and
            opens the triage modal with context from the link. */}
        <PrivateRoute exact path={ROUTE_TRIAGE_LINK} component={TriageLinkLanding} />
        <PrivateRoute path={ROUTE_MONITOR} component={Monitor} />
        <PrivateRoute path={ROUTE_MARKETPLACE} component={Marketplace} />
        {isExpressEnabled && <PrivateRoute path={ROUTE_EXPRESS} component={ExpressView} />}
        {/*
          @TODO: check if it still being necessary
        */}
        {Object.keys(oldToNewMap).map((from) => {
          return (
            <Route
              key={from}
              path={from}
              exact
              render={(props) => {
                let to = oldToNewMap[from];
                if (!to) {
                  return <Redirect to="/" />;
                }

                if (from.includes('/:')) {
                  Object.keys(props.match?.params || {}).forEach((param) => {
                    const paramValue = props.match?.params?.[param] || '';
                    to = to.replace(':' + param, paramValue);
                  });
                }

                if (
                  props.match &&
                  props.match.isExact &&
                  currentPath !== props.location?.pathname
                ) {
                  const pathname = props.location?.pathname || '';
                  setPath(pathname);
                }

                return <Redirect to={to} />;
              }}
            />
          );
        })}
        <Route component={NoRouteMatchView} />
      </Switch>
    </Suspense>
  );
}
